import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSharedViews,
  deleteSharedView,
  type SharedView,
  SHARED_VIEW_MODULES,
} from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';
import CollaborationNav from './CollaborationNav';

export default function SharedViewListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [moduleFilter, setModuleFilter] = useState<string | undefined>();
  const [sharedFilter, setSharedFilter] = useState<string | undefined>();

  const canWrite = hasPermission('collaboration', 'write');
  const canDelete = hasPermission('collaboration', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: Record<string, any> = { ...params };
      if (moduleFilter) filters.module = moduleFilter;
      if (sharedFilter) filters.is_shared = sharedFilter;
      return getSharedViews(filters);
    },
    [moduleFilter, sharedFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('collaboration.shared_view.delete_confirm'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteSharedView(id);
            message.success(t('collaboration.shared_view.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<SharedView> = useMemo(
    () => [
      {
        title: t('collaboration.shared_view.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('collaboration.shared_view.field.module'),
        dataIndex: 'module',
        key: 'module',
        width: 120,
        render: (v: string) => <Tag>{t(`nav.${v}`, v)}</Tag>,
      },
      {
        title: t('collaboration.shared_view.field.owner'),
        dataIndex: 'owner_id',
        key: 'owner_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('collaboration.shared_view.field.is_default'),
        dataIndex: 'is_default',
        key: 'is_default',
        width: 80,
        render: (v: boolean) =>
          v ? <Tag color="blue">{t('app.yes')}</Tag> : <Tag>{t('app.no')}</Tag>,
      },
      {
        title: t('collaboration.shared_view.field.is_shared'),
        dataIndex: 'is_shared',
        key: 'is_shared',
        width: 80,
        render: (v: boolean) =>
          v ? <Tag color="green">{t('app.yes')}</Tag> : <Tag>{t('app.no')}</Tag>,
      },
      {
        title: t('collaboration.shared_view.field.columns_count'),
        key: 'columns_count',
        width: 80,
        render: (_: unknown, record: SharedView) => record.columns?.length ?? 0,
      },
      {
        title: t('common.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 100,
        render: (_: unknown, record: SharedView) => (
          <Space>
            {canWrite && (
              <Tooltip title={t('app.edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/collaboration/shared-views/${record.id}/edit`)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('app.delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canWrite, canDelete, navigate, handleDelete]
  );

  return (
    <div>
      <CollaborationNav />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('collaboration.shared_view.field.module')}
          style={{ width: 160 }}
          value={moduleFilter}
          onChange={setModuleFilter}
          options={SHARED_VIEW_MODULES.map((m) => ({
            value: m,
            label: t(`nav.${m}`, m),
          }))}
        />
        <Select
          allowClear
          placeholder={t('collaboration.shared_view.field.is_shared')}
          style={{ width: 120 }}
          value={sharedFilter}
          onChange={setSharedFilter}
          options={[
            { value: 'true', label: t('app.yes') },
            { value: 'false', label: t('app.no') },
          ]}
        />
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/collaboration/shared-views/new')}
          >
            {t('collaboration.shared_view.new')}
          </Button>
        )}
      </Space>
      <DataGrid
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />
    </div>
  );
}
